import {
  RoundCheckedIcon,
  SubtleButton,
  pushToDataLayer,
  theme,
  usePreventScrollChangeOnElementResize,
  useToast,
  DeprecatedChevronUpIcon,
  DeprecatedChevronDownIcon,
  DeprecatedTellFilledIcon,
  ErrorWarningIcon,
} from '@arnold/common';
import {
  convertQuestionAnswersToTextVersionForGPT,
  extractExplanationFromPartialJson,
  getMessagesForAiSummary,
} from '@arnold/core';
import { AiSummaryLoading } from '../aiSummary/AiSummaryLoading';
import { SURVEY_SUMMARY_PROMPT } from '../aiSummary/summaryInput';
import styled from '@emotion/styled';
import { MD5 } from 'crypto-js';
import { encodeChat } from 'gpt-tokenizer';
import { ChatMessage } from 'gpt-tokenizer/esm/GptEncoding';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Expand from 'react-expand-animated';
import { useTranslation } from 'react-i18next';
import {
  AiSurveySummaryAbInteractionAction,
  FeEventType,
  QuestionWithAnswer,
  Sentiment,
  useAiSurveySummaryQueryQuery,
  useAiSurveySummaryAbTestSubscriptionSubscription,
  useAiSurveySummarySubscriptionSubscription,
  useClearAiSurveySummaryCacheMutation,
  useLogAiSurveySummaryAbInteractionMutation,
  useLogFeEventMutation,
} from '../../generated/hooks';
import { GreenTellIcon, RedTellIcon } from '../sequence/KeyPoint';
import { AiSection } from '../sequence/RespondentSummary';
import { IntercomEvent, IntercomEventSender, sendEventToIntercom } from './ObserverEventSender';
import RateAi from './RateAi';
import { useIsMobileView } from './ResponsiveSwitcher';
import { uniq } from 'ramda';
import { SYS_ADMIN_KEY } from '../../lib/common';
import auth from '../../lib/auth';

const LOCAL_STORAGE_KEY = 'AiSurveySummaryPreviouslyLoaded';

type AiSurveySummaryProps = {
  languageCode: string;
  accessToken: string;
  isProcessSurvey: boolean;
  surveyRespondentsAnswered?: number;
  abTestEnabled?: boolean;
  getReportWidget: (idx: number) => ReactNode;
  answers: QuestionWithAnswer[];
  reportId: string;
};

export const AiSurveySummary = (props: AiSurveySummaryProps) => {
  const [generateSummary, setGenerateSummary] = useState(true);
  const [systemMessage, setSystemMessage] = useState(SURVEY_SUMMARY_PROMPT);
  const [maxTokensK, setMaxTokensK] = useState(Number(process.env.CHATGPT_REPORT_SUMMARY_MAX_TOKENS_K) || 70);
  const [sliderMaxTokensK, setSliderMaxTokensK] = useState(maxTokensK);
  const [reportTokenLength, setReportTokenLength] = useState(0);
  const [shortenedReportTokenLength, setShortenedReportTokenLength] = useState('Not shortened');
  const [timeTook, setTimeTook] = useState(0);
  const [shorteningCoefficient, setShorteningCoefficient] = useState('1.00');

  // Filter out answers based on the max tokens
  const selectedAnswers = useMemo(() => {
    const filteredAndSortedAnswers = props.answers.map((questionWithAnswers) => {
      // question without answers is not a free text question and we do not have anything to shorten
      if (questionWithAnswers.answers == null) return questionWithAnswers;
      const filteredAnswers = questionWithAnswers.answers.filter((answer) => answer.length > 4);
      filteredAnswers.sort((a, b) => {
        if (a.length === b.length) {
          // We need to sort it also alphabetically to have the same hash for the same answers
          return a.localeCompare(b);
        }
        return a.length - b.length;
      });
      return { ...questionWithAnswers, answers: filteredAnswers };
    });

    const timeBefore = Date.now();
    const { shortenedQuestionWithAnswers, numberOfTokensAfterShortening, numberOfTokensBeforeShortening, coefficient } =
      shortenReportToMaxTokens(filteredAndSortedAnswers, maxTokensK, systemMessage);
    const timeAfter = Date.now();
    setTimeTook(timeAfter - timeBefore);
    setShorteningCoefficient(coefficient.toFixed(2));

    setReportTokenLength(numberOfTokensBeforeShortening);
    setShortenedReportTokenLength(
      numberOfTokensBeforeShortening === numberOfTokensAfterShortening
        ? 'NOT SHORTENED'
        : numberOfTokensAfterShortening.toString(),
    );
    return shortenedQuestionWithAnswers;
  }, [props.answers, systemMessage, maxTokensK]);

  if (
    props.answers.every((question) => question.answers?.length === 0 || question.choices?.every((ch) => ch.count === 0))
  ) {
    return null;
  }

  return (
    <>
      {localStorage.getItem('AiSurveyTest') === 'true' && (
        <StyledFrame>
          <StyledHeading>AI survey summary test</StyledHeading>
          <h4>Max tokens adjuster:</h4>
          Here you can adjust the max tokens that should be sent to ChatGPT. We will try to shorten it accordingly, but
          it is not precise. <br />
          It the resulting token length can go up when you decrease the max tokens, because the mechanism that is
          selecting which answers to use is trying to keep the message diverse in length which sometimes results in
          strange behavior for small surveys. The best way to test this is with with report that have more than 80k
          tokens. For example: We are satisfied with the result if the max token limit is set to 70k and the resulting
          token length is between 55k and 85k.
          <HorizontalLayout>
            <input
              type="range"
              min="10"
              max="100"
              step="1"
              value={sliderMaxTokensK}
              onChange={(e) => setSliderMaxTokensK(parseInt(e.target.value, 10))}
              onMouseUp={() => setMaxTokensK(sliderMaxTokensK)}
            />{' '}
            {sliderMaxTokensK}k
          </HorizontalLayout>
          <h3>System message:</h3>
          <textarea
            onChange={(e) => {
              setSystemMessage(e.target.value);
              setGenerateSummary(false);
            }}
            style={{ width: '100%', height: '200px', padding: '5px' }}
          >
            {systemMessage}
          </textarea>
          <i>You can edit the system message to test what the AI is capable of.</i>
          {!generateSummary && <Button onClick={() => setGenerateSummary(true)}>Generate Summary</Button>}
          {generateSummary && <Button onClick={() => setGenerateSummary(false)}>Clear Summary</Button>}
          <h4>Report token length: {reportTokenLength.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</h4>
          <h4>Shortened report token length: {shortenedReportTokenLength.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</h4>
          <h4>Shortening coefficient: {shorteningCoefficient}</h4>
          <h4>Shortening took: {timeTook}ms</h4>
        </StyledFrame>
      )}
      {generateSummary && <SurveySummary {...props} systemMessage={systemMessage} answers={selectedAnswers} />}
    </>
  );
};

const SurveySummary = (props: AiSurveySummaryProps & { systemMessage: string }) => {
  const { t } = useTranslation('SurveySummary');
  const { addBeautifulToast } = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobileView();

  const abTestEnabled = !isMobile && !!props.abTestEnabled;
  const [logAiSurveySummaryAbInteraction] = useLogAiSurveySummaryAbInteractionMutation();
  const exposedForExperimentRef = useRef<Set<string>>(new Set());
  const [abChooserRevealed, setAbChooserRevealed] = useState(false);
  const [selectedVariantKey, setSelectedVariantKey] = useState<'A' | 'B' | null>(null);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [refreshingSummary, setRefreshingSummary] = useState(false);
  const [subscriptionPaused, setSubscriptionPaused] = useState(false);
  const [clearAiSurveySummaryCache] = useClearAiSurveySummaryCacheMutation();
  const viewerSysAdmin = useMemo(
    () => !!localStorage.getItem(SYS_ADMIN_KEY)?.includes(props.accessToken),
    [props.accessToken],
  );
  const activeAbTestEnabled = abTestEnabled && !fallbackMode;

  const firstTimeLoading = useMemo(() => {
    const hash = hashAnswersAndLanguageCode(props.answers, props.languageCode);
    return !localStorage.getItem(LOCAL_STORAGE_KEY)?.includes(hash);
  }, [props.answers, props.languageCode]);

  const baselineSub = useAiSurveySummarySubscriptionSubscription({
    variables: {
      languageCode: props.languageCode,
      accessToken: props.accessToken,
      systemMessage: props.systemMessage,
      answers: props.answers,
    },
    skip: props.answers.length === 0 || activeAbTestEnabled || subscriptionPaused || fallbackMode,
  });

  const abSub = useAiSurveySummaryAbTestSubscriptionSubscription({
    variables: {
      languageCode: props.languageCode,
      accessToken: props.accessToken,
      defaultSystemMessage: props.systemMessage,
      answers: props.answers,
    },
    skip: props.answers.length === 0 || !activeAbTestEnabled || subscriptionPaused || fallbackMode,
  });

  const fallbackQuery = useAiSurveySummaryQueryQuery({
    variables: {
      languageCode: props.languageCode,
      accessToken: props.accessToken,
      systemMessage: props.systemMessage,
      answers: props.answers,
    },
    skip: props.answers.length === 0 || !fallbackMode,
    fetchPolicy: 'network-only',
  });

  const activeSubscriptionError = activeAbTestEnabled ? abSub.error : baselineSub.error;
  const loading = fallbackMode
    ? fallbackQuery.loading || refreshingSummary
    : activeAbTestEnabled
      ? abSub.loading
      : baselineSub.loading;

  const abData = activeAbTestEnabled ? abSub.data?.aiSurveySummaryAbTest ?? null : null;
  const baselineData = fallbackMode
    ? fallbackQuery.data?.aiSurveySummary ?? null
    : !activeAbTestEnabled
      ? baselineSub.data?.aiSurveySummary ?? null
      : null;

  const textSource = activeAbTestEnabled ? (abData?.mode === 'SINGLE' ? abData?.single : null) : baselineData;
  const summaryHash = textSource?.hash;

  const extractedSourceText =
    textSource?.text != null ? extractExplanationFromPartialJson(textSource.text) : textSource?.text;
  const text = useSmoothTextGeneration(extractedSourceText, textSource?.done && !firstTimeLoading, textSource?.done);
  usePreventScrollChangeOnElementResize(ref.current, text);

  const getAnalyticsSnapshot = () => {
    const freeTextAnswersTotal = (props.answers ?? []).reduce((acc, q) => acc + (q.answers?.length ?? 0), 0);
    const answersTotal = (props.answers ?? []).reduce((acc, q) => {
      const choiceAnswers = (q.choices ?? []).reduce((cAcc, ch) => cAcc + (ch.count ?? 0), 0);
      return acc + (q.answers?.length ?? 0) + choiceAnswers;
    }, 0);
    return {
      surveyQuestionsTotal: props.answers?.length ?? 0,
      surveyRespondentsAnswered: props.surveyRespondentsAnswered,
      answersTotal,
      freeTextAnswersTotal,
    };
  };

  const logAbInteraction = async (action: AiSurveySummaryAbInteractionAction, experimentId: string) => {
    const snapshot = getAnalyticsSnapshot();
    await logAiSurveySummaryAbInteraction({
      variables: {
        input: {
          accessToken: props.accessToken,
          experimentId,
          action,
          languageCode: props.languageCode,
          viewerSysAdmin,
          surveyQuestionsTotal: snapshot.surveyQuestionsTotal,
          surveyRespondentsAnswered: snapshot.surveyRespondentsAnswered,
          answersTotal: snapshot.answersTotal,
          freeTextAnswersTotal: snapshot.freeTextAnswersTotal,
        },
      },
    });
  };

  useEffect(() => {
    if (!activeSubscriptionError || fallbackMode) return;
    setFallbackMode(true);
    setAbChooserRevealed(false);
    setSelectedVariantKey(null);
  }, [activeSubscriptionError, fallbackMode]);

  useEffect(() => {
    if (!activeAbTestEnabled) return;
    if (!abData || abData.mode !== 'AB') return;
    if (!abData.experimentId) return;
    const key = String(abData.experimentId);
    if (exposedForExperimentRef.current.has(key)) return;
    exposedForExperimentRef.current.add(key);
    logAbInteraction(AiSurveySummaryAbInteractionAction.Exposed, key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAbTestEnabled, abData?.mode, abData?.experimentId]);

  const revealAbChooser = async () => {
    if (abChooserRevealed) return;
    setAbChooserRevealed(true);
    if (abData?.experimentId) {
      await logAbInteraction(AiSurveySummaryAbInteractionAction.WholeAbSummaryRevealed, String(abData.experimentId));
    }
  };

  const onSelectVariant = async (variantKey: 'A' | 'B') => {
    if (!abData?.experimentId) return;
    await logAbInteraction(
      variantKey === 'A' ? AiSurveySummaryAbInteractionAction.SelectedA : AiSurveySummaryAbInteractionAction.SelectedB,
      String(abData.experimentId),
    );
    setSelectedVariantKey(variantKey);
    setAbChooserRevealed(false);
  };

  const onRefreshSummary = async () => {
    if (!summaryHash || refreshingSummary) {
      return;
    }
    setRefreshingSummary(true);
    try {
      const result = await clearAiSurveySummaryCache({
        variables: {
          accessToken: props.accessToken,
          hash: summaryHash,
        },
      });
      const refreshResult = result.data?.clearAiSurveySummaryCache;
      if (refreshResult?.limitReached) {
        addBeautifulToast({
          message: t('refreshLimitReached'),
          icon: <StyledErrorWarningIcon />,
          duration: 10000,
        });
        return;
      }
      if (refreshResult?.cacheCleared) {
        if (fallbackMode) {
          await fallbackQuery.refetch();
        } else {
          setSubscriptionPaused(true);
        }
      }
    } finally {
      setRefreshingSummary(false);
    }
  };

  useEffect(() => {
    if (subscriptionPaused) {
      setSubscriptionPaused(false);
    }
  }, [subscriptionPaused]);

  const selectedAbTextSource =
    activeAbTestEnabled && abData?.mode === 'AB' && selectedVariantKey
      ? abData.left?.variantKey === selectedVariantKey
        ? abData.left
        : abData.right
      : null;

  const extractedSelectedAbText =
    selectedAbTextSource?.text != null
      ? extractExplanationFromPartialJson(selectedAbTextSource.text)
      : selectedAbTextSource?.text;
  const selectedAbText = useSmoothTextGeneration(
    extractedSelectedAbText,
    selectedAbTextSource?.done && !firstTimeLoading,
    selectedAbTextSource?.done,
  );

  const shouldShowAbChooser = activeAbTestEnabled && abData?.mode === 'AB' && !selectedVariantKey;
  const loadingOrStreaming = (() => {
    if (!activeAbTestEnabled) {
      return loading || !textSource?.done || text.length !== (extractedSourceText?.length ?? 0);
    }
    if (shouldShowAbChooser) {
      return loading || !abData?.left?.done || !abData?.right?.done;
    }
    if (selectedVariantKey) {
      return loading || !selectedAbTextSource?.done || selectedAbText.length !== (extractedSelectedAbText?.length ?? 0);
    }
    return loading || !textSource?.done || text.length !== (extractedSourceText?.length ?? 0);
  })();
  useEffect(() => {
    if (!loadingOrStreaming) {
      if (firstTimeLoading && !shouldShowAbChooser) {
        addBeautifulToast({
          icon: <RoundCheckedIcon />,
          message: t('toastMessageDone'),
          buttonText: t('toastButtonText'),
          duration: 15000,
          buttonOnClick: () => {
            ref.current?.scrollIntoView({ behavior: 'smooth' });
            sendEventToIntercom(
              props.isProcessSurvey
                ? IntercomEvent.PhaseSummaryCompleteToastClicked
                : IntercomEvent.SurveySummaryCompleteToastClicked,
              { accessToken: props.accessToken },
            );
          },
        });
        const hash = hashAnswersAndLanguageCode(props.answers, props.languageCode);
        localStorage.setItem(LOCAL_STORAGE_KEY, (localStorage.getItem(LOCAL_STORAGE_KEY) || '') + ', ' + hash);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingOrStreaming]);

  const finalTextToRender = activeAbTestEnabled && selectedVariantKey ? selectedAbText : text;
  const textArray = finalTextToRender?.split('\n');
  const isPreviouslySelectedAbVariant =
    activeAbTestEnabled && abData?.mode === 'SINGLE' && abData?.experimentId != null;
  const shouldLimitKeyPoints = !(shouldShowAbChooser || !!selectedVariantKey || isPreviouslySelectedAbVariant);
  const Container = shouldShowAbChooser ? AbOuterContainer : BorderContainer;
  return (
    <div ref={ref} className="pt-8">
      <IntercomEventSender
        eventName={props.isProcessSurvey ? IntercomEvent.PhaseSummarySeen : IntercomEvent.SurveySummarySeen}
        data={{ accessToken: props.accessToken }}
      />
      {!loadingOrStreaming && (
        <IntercomEventSender
          eventName={
            props.isProcessSurvey
              ? IntercomEvent.PhaseSummaryFullyLoadedSeen
              : IntercomEvent.SurveySummaryFullyLoadedSeen
          }
          data={{ accessToken: props.accessToken }}
        />
      )}
      <Container>
        <StyledAiSection
          title={t('title')}
          description={t('description')}
          refreshTooltip={t('refreshTooltip')}
          borderless
          centered={shouldShowAbChooser}
          showRefreshButton={
            !shouldShowAbChooser &&
            !loadingOrStreaming &&
            !refreshingSummary &&
            abData?.experimentId == null &&
            !!summaryHash
          }
          onRefresh={onRefreshSummary}
        >
          {loadingOrStreaming && <AiSummaryLoading />}
        </StyledAiSection>

        {shouldShowAbChooser ? (
          <>
            <AbGridWrapper>
              {/* <AbGridFadeOverlay revealed={abChooserRevealed} /> */}
              <AbGrid revealed={abChooserRevealed} data-complete-ai-report-summary={false}>
                <AbVariantCard revealed={abChooserRevealed}>
                  <AbVariantPill>{t('abVariantLabel', { variant: 'A' })}</AbVariantPill>
                  <AbVariantBody>
                    <AiSurveySummaryKeyPointList
                      text={extractExplanationFromPartialJson(abData?.left?.text ?? '')}
                      getReportWidget={props.getReportWidget}
                      isProcessSurvey={props.isProcessSurvey}
                      accessToken={props.accessToken}
                      reportId={props.reportId}
                    />
                  </AbVariantBody>
                  {abChooserRevealed && (
                    <AbVariantCta>
                      <AbVariantCtaLabel>{t('abContinueWithThisVersion')}</AbVariantCtaLabel>
                      <SubtleButton
                        onClick={() => abData?.left?.variantKey && onSelectVariant(abData.left.variantKey)}
                        disabled={!abData?.left?.variantKey}
                      >
                        {t('abSelectButton')}
                      </SubtleButton>
                    </AbVariantCta>
                  )}
                </AbVariantCard>

                <AbVariantCard revealed={abChooserRevealed}>
                  <AbVariantPill>{t('abVariantLabel', { variant: 'B' })}</AbVariantPill>
                  <AbVariantBody>
                    <AiSurveySummaryKeyPointList
                      text={extractExplanationFromPartialJson(abData?.right?.text ?? '')}
                      getReportWidget={props.getReportWidget}
                      isProcessSurvey={props.isProcessSurvey}
                      accessToken={props.accessToken}
                      reportId={props.reportId}
                    />
                  </AbVariantBody>
                  {abChooserRevealed && (
                    <AbVariantCta>
                      <AbVariantCtaLabel>{t('abContinueWithThisVersion')}</AbVariantCtaLabel>
                      <SubtleButton
                        onClick={() => abData?.right?.variantKey && onSelectVariant(abData.right.variantKey)}
                        disabled={!abData?.right?.variantKey}
                      >
                        {t('abSelectButton')}
                      </SubtleButton>
                    </AbVariantCta>
                  )}
                </AbVariantCard>
              </AbGrid>
            </AbGridWrapper>

            {!abChooserRevealed && (
              <AbChooserFooter>
                <AbChooserText>{t('abChooseBestVersion')}</AbChooserText>
                <SubtleButton onClick={() => revealAbChooser()}>{t('abSelectButton')}</SubtleButton>
              </AbChooserFooter>
            )}
          </>
        ) : (
          <div data-complete-ai-report-summary={!loadingOrStreaming}>
            {textArray?.map(
              (line, index) =>
                line &&
                (!shouldLimitKeyPoints || index < 10) && (
                  <KeyPoint
                    key={index}
                    text={line.trim()}
                    getReportWidget={props.getReportWidget}
                    isProcessSurvey={props.isProcessSurvey}
                    accessToken={props.accessToken}
                    reportId={props.reportId}
                    index={index + 1}
                  />
                ),
            )}
          </div>
        )}
      </Container>
      <RateAi type={props.isProcessSurvey ? 'PROCESS_SURVEY_SUMMARY' : 'STANDALONE_SURVEY_SUMMARY'} />
    </div>
  );
};

const AiSurveySummaryKeyPointList = ({
  text,
  ...props
}: {
  text: string;
  getReportWidget: (idx: number) => ReactNode;
  isProcessSurvey: boolean;
  accessToken: string;
  reportId: string;
}) => {
  const lines = (text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <>
      {lines.map((line, index) => (
        <KeyPoint key={index} index={index + 1} text={line} inAbTest={true} {...props} />
      ))}
    </>
  );
};

const AbOuterContainer = styled.div`
  border: none;
  border-radius: 0;
  padding: 0;
`;

const AbGridWrapper = styled.div`
  position: relative;
`;

const AbGrid = styled.div<{ revealed: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.d};
  margin-top: ${theme.spacing.f};
  overflow: hidden;
  max-height: ${({ revealed }) => (revealed ? '10000px' : '350px')};
  transition: max-height 2000ms ease-in;
  -webkit-mask-image: ${({ revealed }) =>
    revealed ? 'none' : 'linear-gradient(to bottom, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 0) 95%)'};
  mask-image: ${({ revealed }) =>
    revealed ? 'none' : 'linear-gradient(to bottom, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 0) 95%)'};
  @media (max-width: ${theme.breakpoint.small}) {
    grid-template-columns: 1fr;
  }
`;

const AbVariantCard = styled.div<{ revealed: boolean }>`
  position: relative;
  z-index: 0;
  border: 2px solid ${theme.colors.ai.default};
  border-radius: 8px;
  background: ${theme.colors.backgroundBasic.default};
  padding: ${theme.spacing.c};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  pointer-events: ${({ revealed }) => (revealed ? 'auto' : 'none')};
  user-select: ${({ revealed }) => (revealed ? 'auto' : 'none')};
`;

const AbVariantPill = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 5px;
  background: ${theme.colors.ai.default};
  color: white;
  font-weight: 400;
  font-size: 12px;
`;

const AbVariantBody = styled.div`
  flex: 1;
`;

const AbVariantCta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.d};
  padding: ${theme.spacing.c};
  margin-top: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.f};
`;

const AbVariantCtaLabel = styled.div`
  font-weight: 600;
  text-align: center;
`;

const AbChooserFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.e};
  margin-top: -${theme.spacing.f};
  margin-bottom: 200px;
  position: relative;
  z-index: 1;
`;

const AbChooserText = styled.div`
  font-weight: 600;
  text-align: center;
`;

const HorizontalLayout = styled.div`
  display: flex;
  gap: ${theme.spacing.f};
`;

const StyledHeading = styled.h2`
  color: ${theme.colors.ai.default};
  font-weight: bold;
`;

const StyledAiSection = styled(AiSection)<{ borderless?: boolean }>`
  & > div {
    border: ${({ borderless }) => (borderless ? 'none' : `1px solid ${theme.colors.ai.default}`)};
  }
`;

const StyledFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.f};
  padding: ${theme.spacing.g};
  align-items: start;
  width: 100%;
  min-height: 100px;
  border: 1px solid ${theme.colors.ai.default};
  border-radius: 5px;
  margin-top: ${theme.spacing.h};
  margin-bottom: ${theme.spacing.h};
  background-color: white;
  box-shadow: inset 0px 0px 5px ${theme.colors.ai.default};
`;

const BorderContainer = styled.div`
  border: 1px solid ${theme.colors.ai.default};
  border-radius: 5px;
  padding: ${theme.spacing.c};
  @media (max-width: ${theme.breakpoint.small}) {
    padding: 0px;
  }
`;

const KeyPoint = ({
  text,
  getReportWidget,
  isProcessSurvey,
  accessToken,
  reportId,
  index,
  inAbTest = false,
}: {
  text: string;
  getReportWidget: (idx: number) => ReactNode;
  isProcessSurvey: boolean;
  accessToken: string;
  reportId: string;
  index: number;
  inAbTest?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobileView();
  const [logFeEvent] = useLogFeEventMutation();

  if (text[0] === '-') {
    // remove the dash at the beginning of the line if there is one
    text = text.slice(1);
  }

  text = text.replace(/]\s?- /g, '] ').trim(); // remove the dash after the bracket if there is one

  if (text.length < 14) return <></>; // skip empty lines with only sentiment on them

  let sentiment: Sentiment;
  if (text.toUpperCase().includes('[POSITIVE]')) {
    sentiment = Sentiment.Positive;
  } else if (text.toLocaleUpperCase().includes('[NEGATIVE]')) {
    sentiment = Sentiment.Negative;
  } else {
    sentiment = Sentiment.Neutral;
  }
  text = text.replace(/\[POSITIVE\]|\[NEUTRAL\]|\[NEGATIVE\]/gi, '');

  const questionIndexes: number[] = [];
  if (text.includes('[')) {
    if (text.includes(']')) {
      // get the indexes between the brackets
      questionIndexes.push(
        ...(text
          .match(/\[(.*?)\]/)?.[1]
          ?.split(',')
          .map((i) => parseInt(i, 10))
          .filter((i) => !isNaN(i)) || []),
      );
      // remove the brackets with the ID
      text = text.replace(/\s*\[(.*?)\]\s*/g, '');
    } else {
      // remove the bracket at the end of the line and anything after the bracket
      text = text.split('[')[0];
    }
  }

  const clickHandler = () => {
    const userId = auth.getUserId();
    const itemColor = sentiment === Sentiment.Positive ? 'green' : sentiment === Sentiment.Negative ? 'red' : 'yellow';
    if (!expanded) {
      sendEventToIntercom(isProcessSurvey ? IntercomEvent.PhaseSummaryOpened : IntercomEvent.SurveySummaryOpened, {
        accessToken,
      });
      logFeEvent({
        variables: {
          eventType: FeEventType.ReportAiSurveySummaryOpened,
          payload: {
            reportAccessId: reportId,
          },
        },
      });
      pushToDataLayer({
        event: 'ai_recommendation_expanded',
        hit: {
          event_type: 'generic_event',
          action_name: 'expand',
          ...(userId && { user_id: userId }),
          item_index: index,
          item_color: itemColor,
          item_text_preview: text.slice(0, 50),
        },
        _clear: true,
      });
    } else {
      pushToDataLayer({
        event: 'ai_recommendation_collapsed',
        hit: {
          event_type: 'generic_event',
          action_name: 'collapse',
          ...(userId && { user_id: userId }),
          item_index: index,
        },
        _clear: true,
      });
    }
    setExpanded((prev) => !prev);
  };

  return (
    <>
      <KeyPointWrapper
        inAbTest={inAbTest}
        style={{ minHeight: '66px' }}
        className={`bg-white rounded-lg shadow-sm py-5 d-flex flex-column ${inAbTest ? 'mt-3' : 'mt-4'}`}
        onClick={() => {
          if (!isMobile) return;
          clickHandler();
        }}
      >
        <div className="d-flex align-items-center" style={{ flexGrow: 1 }}>
          <SentimentWrapper className="px-4">
            {sentiment === Sentiment.Negative && <RedTellIcon />}
            {sentiment === Sentiment.Positive && <GreenTellIcon />}
            {sentiment === Sentiment.Neutral && <DeprecatedTellFilledIcon />}
          </SentimentWrapper>
          <div className={isMobile ? 'ml-4' : 'ml-6'}>
            <p className="mb-0 mr-4">{text}</p>
          </div>
          <ShowQuestionsContainer data-pptx-hide>
            {!!questionIndexes.length && (
              <SubtleButton
                onClick={() => {
                  if (isMobile) return;
                  clickHandler();
                }}
                size="small"
                leftIcon={expanded ? <DeprecatedChevronUpIcon /> : <DeprecatedChevronDownIcon />}
                iconColor={theme.colors.text.secondary}
              />
            )}
          </ShowQuestionsContainer>
        </div>
        <Expand open={expanded} duration={500} transitions={['height', 'opacity']}>
          {uniq(questionIndexes).map((questionIndex) => {
            return <QuestionContainer key={questionIndex}>{getReportWidget(questionIndex)}</QuestionContainer>;
          })}
        </Expand>
      </KeyPointWrapper>
    </>
  );
};

const KeyPointWrapper = styled.div<{ inAbTest?: boolean }>`
  padding-left: ${({ inAbTest }) => (inAbTest ? theme.spacing.d : theme.spacing.h)};
  padding-right: ${({ inAbTest }) => (inAbTest ? theme.spacing.d : theme.spacing.h)};
  @media (max-width: ${theme.breakpoint.small}) {
    cursor: pointer;
    padding-left: ${theme.spacing.d};
    padding-right: 1.5rem;
  }
  animation: appearing 1s;
  @keyframes appearing {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
const SentimentWrapper = styled.div`
  @media (max-width: ${theme.breakpoint.small}) {
    & > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ShowQuestionsContainer = styled.div`
  justify-self: flex-end;
  padding-left: 8px;
  flex-grow: 1;
  justify-items: right;
  @media (max-width: ${theme.breakpoint.small}) {
    display: none;
  }
`;

const QuestionContainer = styled.div`
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  margin-top: ${theme.spacing.g};
  container-type: inline-size;
`;

const useSmoothTextGeneration = (text: string | null | undefined, disabled = false, fast = false) => {
  const [smoothText, setSmoothText] = useState<string>(text || '');
  const [timeoutRunning, setTimeoutRunning] = useState(false);
  if (disabled) return text || '';
  if (!text) return '';
  if (text === smoothText) return text;
  if (!text.includes(smoothText)) {
    setSmoothText(text);
    return text;
  }
  if (!timeoutRunning) {
    setTimeoutRunning(true);
    let delay = fast ? 3 : 1000 / Math.pow((text.length - smoothText.length) / 3, 2);
    delay = Math.max(2, delay);
    setTimeout(() => {
      setTimeoutRunning(false);
      setSmoothText((prev) => prev + text[prev.length]);
    }, delay);
  }
  return smoothText;
};

const hashAnswersAndLanguageCode = (answers: QuestionWithAnswer[], languageCode: string) => {
  return MD5(JSON.stringify(answers) + languageCode).toString();
};

/**
 * Evenly removes elements from an array based on a percentage.
 * Examples:
 * shortenArray([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 50) => [1, 3, 5, 7, 9]
 * shortenArray([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 25) => [1, 4, 7]
 * shortenArray([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 10) => [1]
 */
const shortenArray = <T extends unknown>(arr: T[], percentage: number): T[] => {
  if (percentage <= 0) {
    return [];
  }

  if (percentage >= 100) {
    return arr;
  }

  // Calculate the number of elements to keep
  const numElementsToKeep = Math.ceil((percentage / 100) * arr.length);
  if (numElementsToKeep === arr.length) {
    // if the percentage is too high to remove even one element, but still not 100%, we need to remove the first element
    return arr.slice(1);
  }
  // Calculate the interval between elements to keep
  const interval = Math.floor(arr.length / numElementsToKeep);
  const result: T[] = [];

  // Adjust interval dynamically to distribute elements evenly
  for (let i = 0; i < numElementsToKeep; i++) {
    result.push(arr[Math.min(i * interval, arr.length - 1)]);
  }

  return result;
};

const shortenReportToMaxTokens = (
  filteredAndSortedAnswers: QuestionWithAnswer[],
  maxTokensK: number,
  systemMessage: string,
) => {
  const { coefficient, realTokens } = getTokenCoefficient(filteredAndSortedAnswers, systemMessage);
  let shortenedQuestionWithAnswers = filteredAndSortedAnswers;
  let currentTokens = cheaplyGetTokensForAnswers(shortenedQuestionWithAnswers, systemMessage, coefficient);

  while (currentTokens > maxTokensK * 1000) {
    // compute the percentage of tokens to remove
    const percentageToRemove = (1 - (maxTokensK * 1000) / currentTokens) * 100;
    const saferPercentageToRemove = Math.max(Math.min(percentageToRemove, 40), 4);
    // We want to remove every time only part of the difference, coz the messages are not the same length, it is safer this way
    const percentageToKeep = 100 - saferPercentageToRemove / 4;
    // remove 10% of the answers
    shortenedQuestionWithAnswers = shortenedQuestionWithAnswers.map((question) => {
      if (question.answers) {
        const shortenedAnswers = shortenArray(question.answers, percentageToKeep);
        return { ...question, answers: shortenedAnswers };
      }
      return question;
    });
    const currentTokensNew = cheaplyGetTokensForAnswers(shortenedQuestionWithAnswers, systemMessage, coefficient);
    currentTokens = currentTokensNew;
  }

  const numberOfTokensAfterShortening = getTokensForAnswers(shortenedQuestionWithAnswers, systemMessage);
  return {
    shortenedQuestionWithAnswers,
    numberOfTokensAfterShortening,
    numberOfTokensBeforeShortening: realTokens,
    coefficient,
  };
};

/**
 * This is very expensive operation, chat encoding is very slow and can take 100ms for large reports.
 * It should be used sparingly
 */
const getTokensForAnswers = (answers: QuestionWithAnswer[], systemMessage: string) => {
  const messages = getMessagesForAiSummary(convertQuestionAnswersToTextVersionForGPT(answers), systemMessage);
  const encoded = encodeChat(messages as ChatMessage[], (process.env.CHATGPT_REPORT_SUMMARY_MODEL as any) || 'gpt-4o');
  return encoded.length;
};

/**
 * This is more faster version of getTokensForAnswers, it is not 100% accurate, but it is good enough guess that should be within 5% of the real value if used with coefficient
 * Coefficient is used to adjust the value to be more accurate, you can get it by dividing the text length by the number of tokens of the original text
 */
const cheaplyGetTokensForAnswers = (answers: QuestionWithAnswer[], systemMessage: string, coefficient = 2.2) => {
  const messages = getMessagesForAiSummary(convertQuestionAnswersToTextVersionForGPT(answers), systemMessage);
  const tokens = messages.reduce((acc, mes) => acc + mes.content.length, 0) / coefficient;
  return tokens;
};

const getTokenCoefficient = (answers: QuestionWithAnswer[], systemMessage: string) => {
  const tokens = getTokensForAnswers(answers, systemMessage);
  const cheapTokens = cheaplyGetTokensForAnswers(answers, systemMessage, 1);
  return { coefficient: cheapTokens / tokens, realTokens: tokens, cheapTokens };
};

const StyledErrorWarningIcon = styled(ErrorWarningIcon)`
  & path {
    fill: white;
  }
`;
